import { useEffect, useId, useState, type ReactNode } from 'react';

// Basic building blocks. Interactive elements are at least 44 px high (PROMPT.md §9).
// Soft 3D (ADR-0028): cards and buttons are raised, fields and tracks are pressed in.

export function cx(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

export function Card({
  title,
  action,
  children,
  className,
}: {
  title?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cx('rounded-3xl bg-surface p-5 shadow-raised sm:p-6', className)}>
      {(title || action) && (
        <header className="mb-4 flex items-center justify-between gap-3">
          {title && <h2 className="text-xs font-semibold tracking-[0.08em] text-muted uppercase">{title}</h2>}
          {action}
        </header>
      )}
      {children}
    </section>
  );
}

export function PageHeader({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">{title}</h1>
      {children}
    </div>
  );
}

export function Stat({
  label,
  value,
  tone = 'default',
  hint,
}: {
  label: string;
  value: string;
  tone?: 'default' | 'accent' | 'warm' | 'muted';
  hint?: string;
}) {
  const color = {
    default: 'text-text',
    accent: 'text-accent-text',
    warm: 'text-warm-text',
    muted: 'text-muted',
  }[tone];
  // formatted values separate number and unit with a narrow no-break space (packages/shared format.ts)
  const split = value.lastIndexOf(' ');
  const number = split > 0 ? value.slice(0, split) : value;
  const unit = split > 0 ? value.slice(split + 1) : undefined;
  return (
    <div className="min-w-0">
      <div className="text-sm text-muted">{label}</div>
      <div className={cx('mt-1 flex flex-wrap items-baseline gap-x-1', color)}>
        <span className="text-3xl font-semibold tracking-tight whitespace-nowrap tabular-nums sm:text-4xl">
          {number}
        </span>
        {unit && (
          <span className="text-base font-medium whitespace-nowrap text-muted sm:text-lg">{unit}</span>
        )}
      </div>
      {hint && <div className="mt-1 text-sm text-muted">{hint}</div>}
    </div>
  );
}

export function Button({
  children,
  variant = 'primary',
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'ghost' }) {
  const styles = {
    primary: 'bg-accent text-on-accent shadow-raised-sm hover:brightness-110 disabled:opacity-50',
    secondary: 'bg-surface text-text shadow-raised-sm hover:text-accent-text disabled:opacity-50',
    ghost: 'text-muted hover:text-text',
  }[variant];
  return (
    <button
      type="button"
      className={cx(
        'inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl px-4 font-medium transition active:shadow-inset',
        styles,
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

export interface SegmentOption<T extends string> {
  value: T;
  label: string;
}

export function SegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
  disabled,
}: {
  label: string;
  options: SegmentOption<T>[];
  value: T | undefined;
  onChange(value: T): void;
  disabled?: boolean;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="grid auto-cols-fr grid-flow-col gap-1 rounded-2xl bg-surface-2 p-1 shadow-inset"
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={() => !active && onChange(option.value)}
            className={cx(
              'min-h-11 rounded-xl px-2 text-sm font-medium transition sm:text-base',
              active
                ? 'bg-surface font-semibold text-accent-text shadow-raised-sm'
                : 'text-muted hover:text-text',
              disabled && 'cursor-not-allowed opacity-60',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/** range slider that only commits on release, so dragging does not spam commands */
export function SliderSetting({
  label,
  value,
  min,
  max,
  step = 1,
  format,
  onCommit,
  disabled,
  hint,
}: {
  label: string;
  value: number | undefined;
  min: number;
  max: number;
  step?: number;
  format(value: number): string;
  onCommit(value: number): void;
  disabled?: boolean;
  hint?: string;
}) {
  const id = useId();
  const [draft, setDraft] = useState(value ?? min);
  useEffect(() => setDraft(value ?? min), [value, min]);
  const commit = () => draft !== value && onCommit(draft);
  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <label htmlFor={id} className="text-sm text-muted">
          {label}
        </label>
        <span className="text-lg font-semibold tabular-nums">{format(draft)}</span>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={draft}
        disabled={disabled}
        onChange={(e) => setDraft(Number(e.target.value))}
        onPointerUp={commit}
        onKeyUp={commit}
        onBlur={commit}
        className="h-11 w-full cursor-pointer accent-[var(--accent)] disabled:cursor-not-allowed disabled:opacity-50"
      />
      {hint && <p className="mt-1 text-sm text-muted">{hint}</p>}
    </div>
  );
}

export function Toggle({
  label,
  checked,
  onChange,
  disabled,
  hint,
}: {
  label: string;
  checked: boolean;
  onChange(value: boolean): void;
  disabled?: boolean;
  hint?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <div className="font-medium">{label}</div>
        {hint && <p className="mt-1 text-sm text-muted">{hint}</p>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cx(
          'relative inline-flex h-11 w-[4.25rem] shrink-0 items-center rounded-full shadow-inset transition',
          checked ? 'bg-accent' : 'bg-surface-2',
          disabled && 'opacity-50',
        )}
      >
        <span
          className={cx(
            'inline-block h-8 w-8 rounded-full bg-white shadow-md transition',
            checked ? 'translate-x-8' : 'translate-x-1',
          )}
        />
      </button>
    </div>
  );
}

export function Banner({
  tone,
  title,
  children,
  icon,
}: {
  tone: 'warm' | 'accent' | 'danger' | 'muted';
  title: string;
  children?: ReactNode;
  icon?: ReactNode;
}) {
  const styles = {
    warm: 'border-warm/40 bg-warm-soft text-text',
    accent: 'border-accent/40 bg-accent-soft text-text',
    danger: 'border-danger/40 bg-danger/10 text-text',
    muted: 'border-border bg-surface-2 text-text',
  }[tone];
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cx('flex gap-3 rounded-2xl border p-4', styles)}
    >
      {icon && <div className="mt-0.5 shrink-0">{icon}</div>}
      <div>
        <div className="font-semibold">{title}</div>
        {children && <div className="mt-1 text-sm text-muted">{children}</div>}
      </div>
    </div>
  );
}

export function Spinner({ label }: { label: string }) {
  return (
    <div role="status" className="flex items-center gap-3 text-muted">
      <span className="h-5 w-5 animate-spin rounded-full border-2 border-border border-t-accent" />
      {label}
    </div>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <p className="py-6 text-center text-muted">{children}</p>;
}
